import {Link, useOutletContext, useRouteLoaderData} from '@remix-run/react'
import clsx from 'clsx'

import type {FunctionComponent} from 'react'
import {useEffect, useMemo, useRef, useState} from 'react'
import {useReportCustomEvent, useReportWidgetClick} from '~/hooks/analytic-hooks'
import {IconChevronRight} from '~/icons/brg-icon-chevron-right'
import type {RootAndListContext, RootLoaderResponse} from '~/types'

const DEFAULT_BREADCRUMBS = [
  {
    url: '/',
    name: 'Home',
  },
  {
    url: '/categories',
    name: 'All Categories',
  },
]

type Breadcrumb = {
  url: string
  name: string
}

const CATEGORIES_URL_PREFIX = '/categories/'

const BreadcrumbsHeaderMaga: FunctionComponent = () => {
  const {
    dataResponse: {productsCategory, breadcrumbs = []},
    displayCookieConsent,
  } = useOutletContext<RootAndListContext>()
  const {deviceType, featureFlags, hostname} = useRouteLoaderData('root') as RootLoaderResponse
  const [breadcrumbsToFitMobile, setBreadcrumbsToFitMobile] = useState<typeof fullBreadcrumbs>([])
  const breadcrumbsContainerRef = useRef<HTMLDivElement>(null)
  const [onHoverOnceReportedElements, setOnHoverOnceReportedElements] = useState<string[]>([])

  const reportWidgetClick = useReportWidgetClick()
  const reportCustomEvent = useReportCustomEvent()

  const fullBreadcrumbs = useMemo(() => {
    const currentBreadcrumb = {
      url: '#',
      name: productsCategory?.name,
    }

    return [
      ...DEFAULT_BREADCRUMBS,
      ...breadcrumbs.map((breadcrumb) => ({
        name: breadcrumb.name,
        url: `${CATEGORIES_URL_PREFIX}${breadcrumb.slug}`,
      })),
      currentBreadcrumb,
    ]
  }, [breadcrumbs, productsCategory?.name])

  const [lastBreadcrumb] = fullBreadcrumbs.slice(-1)

  function isLastBreadcrumb(currIndex: number) {
    return currIndex === fullBreadcrumbs.length - 1
  }

  useEffect(() => {
    if (!window) return

    const containerWidthChars = (breadcrumbsContainerRef.current?.clientWidth || window.innerWidth - 200) / 8

    const breadcrumbsToFitMobile: Breadcrumb[] = []
    const breadcrumbsReversed = [...fullBreadcrumbs].reverse()

    breadcrumbsReversed.forEach((bc, index) => {
      if (breadcrumbsToFitMobile.length > 0 && breadcrumbsToFitMobile[breadcrumbsToFitMobile.length - 1].name === '...')
        return

      const currentLength = breadcrumbsToFitMobile.reduce((acc, curr) => acc + curr.name.length, 0)
      if (currentLength + bc.name.length + index * 8 <= containerWidthChars) {
        breadcrumbsToFitMobile.push(bc)
      } else {
        breadcrumbsToFitMobile.push({
          url: bc.url,
          name: '...',
        })
      }
    })

    setBreadcrumbsToFitMobile(breadcrumbsToFitMobile.reverse())
  }, [fullBreadcrumbs, breadcrumbsContainerRef])

  return (
    breadcrumbs.length !== 0 && (
      <div ref={breadcrumbsContainerRef} className="z-10 h-4 mobile:line-clamp-1">
        {/** ------------------ desktop breadcrumbs -----------------*/}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'BreadcrumbList',
              itemListElement: fullBreadcrumbs.map((breadcrumb, index) => {
                const path = breadcrumb.url === '#' ? `/${productsCategory?.slug}` : breadcrumb.url
                return {
                  '@type': 'ListItem',
                  position: index + 1,
                  item: {
                    '@id': `https://${hostname}${path}`,
                    name: breadcrumb.name,
                  },
                }
              }),
            }),
          }}
        ></script>
        <div
          data-testid="breadcrumbs"
          className="flex flex-wrap items-center gap-1 text-sm leading-4 text-white last:cursor-default tablet:hidden"
        >
          {fullBreadcrumbs.map((breadcrumb, index) => {
            const urlBreadcrumbLink = isLastBreadcrumb(index) ? '#' : breadcrumb.url
            return (
              <div key={`${breadcrumb}-${index}`} className="flex flex-row items-center gap-2">
                <Link
                  reloadDocument
                  to={urlBreadcrumbLink}
                  aria-label={`go to ${breadcrumb.name}`}
                  className="text-xs capitalize"
                  onClick={() => {
                    reportWidgetClick({
                      widget_name: 'breadcrumbs',
                      widget_place: 'top',
                      trigger_type: urlBreadcrumbLink,
                      variation: breadcrumb.name,
                    })
                  }}
                  onMouseEnter={() => {
                    if (deviceType === 'desktop' && !onHoverOnceReportedElements.includes(breadcrumb.name)) {
                      reportCustomEvent('hover', {
                        elementName: 'breadcrumbs',
                        placement: 'top',
                        variation: breadcrumb.name,
                      })
                      setOnHoverOnceReportedElements([...onHoverOnceReportedElements, breadcrumb.name])
                    }
                  }}
                >{`${breadcrumb.name}`}</Link>
                {isLastBreadcrumb(index) ? null : (
                  <div className="flex">
                    <IconChevronRight fill="white" className="size-3" />
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/** ------------------ mobile breadcrumbs -----------------*/}
        {featureFlags?.showNewHeaderAndCookieUi && deviceType && deviceType !== 'desktop' ? (
          <div className="hidden flex-nowrap items-center gap-2 text-sm leading-4 text-white last:cursor-default tablet:flex">
            {breadcrumbsToFitMobile.map((breadcrumb) => {
              const isLastBreadcrumb = breadcrumb.name === lastBreadcrumb.name
              return (
                <section key={breadcrumb.name} className="flex flex-row">
                  <Link reloadDocument to={breadcrumb.url} key={breadcrumb.name} className="text-xs">
                    {breadcrumb.name}
                  </Link>
                  {!isLastBreadcrumb ? (
                    <div className="flex pl-1 pr-1 text-xs">
                      <IconChevronRight className="size-3" fill="white" />
                    </div>
                  ) : null}
                </section>
              )
            })}
          </div>
        ) : (
          lastBreadcrumb && (
            <div
              className={clsx(
                'hidden flex-wrap items-center gap-1 text-sm leading-4 text-white last:cursor-default tablet:flex',
                displayCookieConsent &&
                  !featureFlags?.showNewHeaderAndCookieUi &&
                  deviceType &&
                  deviceType !== 'desktop' &&
                  'invisible',
              )}
            >
              <div className="flex flex-row">
                <Link
                  reloadDocument
                  to="/categories"
                  className="text-xs"
                  aria-label="go to categories"
                  onClick={() => {
                    reportWidgetClick({
                      widget_name: 'breadcrumbs',
                      widget_place: 'top',
                      trigger_type: '/categories',
                    })
                  }}
                >
                  ...
                </Link>
                <div className="flex items-center pl-1 pr-1">
                  <IconChevronRight className="size-3" fill="white" />
                </div>
              </div>

              <div key={`${lastBreadcrumb}-${fullBreadcrumbs.length - 1}`} className="flex flex-row items-center">
                <Link
                  reloadDocument
                  to={lastBreadcrumb.url}
                  aria-label={`go to ${lastBreadcrumb.name}`}
                  className="text-xs capitalize"
                  onClick={() => {
                    reportWidgetClick({
                      widget_name: 'breadcrumbs',
                      widget_place: 'top',
                      trigger_type: lastBreadcrumb.url,
                      variation: lastBreadcrumb.name,
                    })
                  }}
                  onMouseEnter={() => {
                    if (deviceType === 'desktop' && !onHoverOnceReportedElements.includes(lastBreadcrumb.name)) {
                      reportCustomEvent('hover', {
                        elementName: 'breadcrumbs',
                        placement: 'top',
                        variation: lastBreadcrumb.name,
                      })
                      setOnHoverOnceReportedElements([...onHoverOnceReportedElements, lastBreadcrumb.name])
                    }
                  }}
                >{`${lastBreadcrumb.name}`}</Link>
              </div>
            </div>
          )
        )}
      </div>
    )
  )
}

export default BreadcrumbsHeaderMaga
